import type {
    AuraAskRequest,
    AuraAskResponse,
    AuraContextResponse,
    AuraConversation,
    AuraHistoryResponse,
    AuraMessage,
    AuraProcessingCostsResponse,
    AuraProcessingCostsFilters,
} from '@/types';
import BaseService from '@/services/api/BaseService';

/**
 * AURA AI Service
 * Handles all API communication with AURA backend endpoints
 */
class AuraService extends BaseService<AuraConversation> {
    /**
     * Load chat context for the current level
     */
    async getContext(): Promise<AuraContextResponse> {
        const { data } = await this.http.get('/aura/context');
        return data;
    }

    /**
     * Send question to AURA and get response
     */
    async ask(request: AuraAskRequest): Promise<AuraAskResponse> {
        const { data } = await this.http.post('/aura/ask', request);
        return data.data; // Backend returns { success, data: {...} }
    }

    /**
     * Get conversation history
     */
    async getHistory(page = 1, limit = 20): Promise<AuraHistoryResponse> {
        const { data } = await this.http.get('/aura/history', {
            params: { page, limit },
        });
        return data;
    }

    async getProcessingCosts(
        filters: AuraProcessingCostsFilters = {},
    ): Promise<AuraProcessingCostsResponse> {
        const { data } = await this.http.get('/aura/processing-costs', { params: filters });
        return data;
    }

    /**
     * Create new conversation
     */
    async createConversation(
        title?: string,
        initialQuestion?: string,
    ): Promise<{ conversation: AuraConversation }> {
        const { data } = await this.http.post('/aura/conversation', {
            title,
            initialQuestion,
        });
        return data.data; // Backend returns { success, data: { conversation } }
    }

    /**
     * Get conversation by ID with messages
     */
    async getConversation(
        id: number,
    ): Promise<{ conversation: AuraConversation; messages: AuraMessage[] }> {
        const { data } = await this.http.get(`/aura/conversation/${id}`);
        return data.data; // Backend returns { success, data: { conversation, messages } }
    }

    /**
     * Delete conversation by ID
     */
    async deleteConversation(id: number): Promise<void> {
        await this.http.delete(`/aura/conversation/${id}`);
    }

    /**
     * Delete specific messages in a conversation
     */
    async deleteConversationMessages(conversationId: number, messageIds: number[]): Promise<void> {
        await this.http.delete(`/aura/conversation/${conversationId}/messages`, {
            data: { messageIds },
        });
    }
}

export default new AuraService('aura');
